import React, { useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import toast from 'react-hot-toast';
import { authHeader } from '../auth';
import AccessGateModal from '../components/AccessGateModal';
import useAccessGate from '../hooks/useAccessGate';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  color: #2d3748;
  font-size: 2.2rem;
  font-weight: 700;
  text-align: left;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2d3748;
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: 0.9rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.9rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 0.9rem 2rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingIcon = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

const ResultsContainer = styled.div`
  margin-top: 2rem;
  display: grid;
  gap: 1.5rem;
`;

const ScoreCard = styled.div`
  background: linear-gradient(
    135deg,
    ${(props) =>
      props.score >= 80 ? '#48bb78' : props.score >= 60 ? '#ed8936' : '#f56565'},
    ${(props) =>
      props.score >= 80 ? '#38a169' : props.score >= 60 ? '#dd6b20' : '#e53e3e'}
  );
  color: white;
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const ScoreNumber = styled.div`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const ScoreLabel = styled.div`
  font-size: 1.1rem;
  opacity: 0.95;
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const AnalysisCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${(props) => props.color || '#667eea'};
`;

const AnalysisTitle = styled.h3`
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  padding: 0.45rem 0;
  color: #4a5568;
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '${(props) => props.icon || '•'}';
    position: absolute;
    left: 0;
    color: ${(props) => props.color || '#667eea'};
    font-weight: bold;
  }
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const BreakdownItem = styled.div`
  background: #f7fafc;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
`;

const BreakdownScore = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) =>
    props.score >= 80 ? '#48bb78' : props.score >= 60 ? '#ed8936' : '#f56565'};
  margin-bottom: 0.5rem;
`;

const BreakdownLabel = styled.div`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
`;

const BreakdownComment = styled.div`
  font-size: 0.9rem;
  color: #718096;
`;

const JobMatch = () => {
  const { gate, requirePremium, closeGate } = useAccessGate();
  const [formData, setFormData] = useState({
    jobTitle: '',
    company: '',
    jobDescription: '',
    cvText: ''
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allowed = await requirePremium();
    if (!allowed) return;
    setLoading(true);
    setResults(null);

    try {
      const response = await api.post('/api/job-match/analyze', formData, {
        headers: authHeader()
      });
      setResults(response.data);
      toast.success('Analiz tamamlandı');
    } catch (error) {
      console.error('Job match analysis error:', error);
      toast.error(error.response?.data?.error || 'Analiz sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Mükemmel Uyum';
    if (score >= 60) return 'İyi Uyum';
    if (score >= 40) return 'Orta Uyum';
    return 'Düşük Uyum';
  };

  return (
    <PageWrapper>
      <Container>
        <Card>
          <Title>İlan Uyum Analizi</Title>
          <Subtitle>
            CV'nizi İş ilanı ile karşılaştırın; uyum puanınızı, gerekli yetkinliklerinizi ve
            geliştirmeniz gereken alanları görün.
          </Subtitle>

          <Form onSubmit={handleSubmit}>
            <TwoColumnGrid>
              <FormGroup>
                <Label htmlFor="jobTitle">İş İlanı Başlığı</Label>
                <Input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  placeholder="Örn: Frontend Developer"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="company">Şirket Adı</Label>
                <Input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Örn: TechCorp"
                />
              </FormGroup>
            </TwoColumnGrid>

            <FormGroup>
              <Label htmlFor="jobDescription">İş İlanı Açıklaması</Label>
              <TextArea
                id="jobDescription"
                name="jobDescription"
                value={formData.jobDescription}
                onChange={handleChange}
                placeholder="İş ilanının tam metnini buraya yapıştırın..."
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="cvText">CV Metni</Label>
              <TextArea
                id="cvText"
                name="cvText"
                value={formData.cvText}
                onChange={handleChange}
                placeholder="CV'nizin metin halini buraya yapıştırın..."
                required
              />
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingIcon />
                  Analiz Ediliyor...
                </>
              ) : (
                'Analiz Et'
              )}
            </Button>
          </Form>

          {results && (
            <ResultsContainer>
              <ScoreCard score={results.matchScore}>
                <ScoreNumber>{results.matchScore}%</ScoreNumber>
                <ScoreLabel>{getScoreLabel(results.matchScore)}</ScoreLabel>
              </ScoreCard>

              <AnalysisGrid>
                <AnalysisCard color="#48bb78">
                  <AnalysisTitle>Güçlü Yönler</AnalysisTitle>
                  <List>
                    {(results.analysis.strengths || []).map((item, index) => (
                      <ListItem key={index} color="#48bb78" icon="✓">
                        {item}
                      </ListItem>
                    ))}
                  </List>
                </AnalysisCard>

                <AnalysisCard color="#f56565">
                  <AnalysisTitle>Zayıf Yönler</AnalysisTitle>
                  <List>
                    {(results.analysis.weaknesses || []).map((item, index) => (
                      <ListItem key={index} color="#f56565" icon="!">
                        {item}
                      </ListItem>
                    ))}
                  </List>
                </AnalysisCard>

                <AnalysisCard color="#ed8936">
                  <AnalysisTitle>Eksik Beceriler</AnalysisTitle>
                  <List>
                    {(results.analysis.missingSkills || []).map((item, index) => (
                      <ListItem key={index} color="#ed8936" icon="•">
                        {item}
                      </ListItem>
                    ))}
                  </List>
                </AnalysisCard>

                <AnalysisCard color="#667eea">
                  <AnalysisTitle>Genel Öneriler</AnalysisTitle>
                  <List>
                    {(results.analysis.recommendations || []).map((item, index) => (
                      <ListItem key={index} color="#667eea" icon="→">
                        {item}
                      </ListItem>
                    ))}
                  </List>
                </AnalysisCard>
              </AnalysisGrid>

              {results.detailedBreakdown && (
                <AnalysisCard>
                  <AnalysisTitle>Detaylı Analiz</AnalysisTitle>
                  <BreakdownGrid>
                    {Object.entries(results.detailedBreakdown).map(([key, value]) => (
                      <BreakdownItem key={key}>
                        <BreakdownScore score={value.score}>
                          {value.score}%
                        </BreakdownScore>
                        <BreakdownLabel>
                          {key === 'technicalSkills' && 'Teknik Beceriler'}
                          {key === 'experience' && 'Deneyim'}
                          {key === 'education' && 'Eğitim'}
                          {key === 'softSkills' && 'Yumuşak Beceriler'}
                        </BreakdownLabel>
                        <BreakdownComment>{value.comment}</BreakdownComment>
                      </BreakdownItem>
                    ))}
                  </BreakdownGrid>
                </AnalysisCard>
              )}
            </ResultsContainer>
          )}
        </Card>
      </Container>
      <AccessGateModal type={gate} onClose={closeGate} />
    </PageWrapper>
  );
};

export default JobMatch;
